import React, { useEffect, useMemo, useState, type MouseEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { useAuth } from '../../context/AuthProvider';

function asText(value: unknown): string {
  return typeof value === 'string' ? value.trim() : '';
}

function initialsFromName(value: string): string {
  const trimmed = asText(value);
  if (!trimmed) return '';
  const parts = trimmed.split(/\s+/).filter(Boolean);
  if (!parts.length) return '';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0] ?? ''}${parts[1][0] ?? ''}`.toUpperCase();
}

function initialsFromEmail(value: string): string {
  const trimmed = asText(value);
  if (!trimmed) return '';
  const local = trimmed.split('@')[0]?.replace(/[^a-zA-Z0-9]/g, '') ?? '';
  if (!local) return '';
  return local.slice(0, 2).toUpperCase();
}

export function getPortalAvatarInitials(identity: {
  firstName?: string | null;
  lastName?: string | null;
  name?: string | null;
  email?: string | null;
}): string {
  const first = asText(identity.firstName);
  const last = asText(identity.lastName);
  if (first || last) {
    const merged = `${first} ${last}`.trim();
    const initials = initialsFromName(merged);
    if (initials) return initials;
  }

  const name = asText(identity.name);
  if (name) {
    const initials = initialsFromName(name);
    if (initials) return initials;
  }

  const fromEmail = initialsFromEmail(asText(identity.email));
  return fromEmail || '??';
}

export function getPortalAvatarLabel(identity: {
  firstName?: string | null;
  lastName?: string | null;
  name?: string | null;
  email?: string | null;
}): string {
  const first = asText(identity.firstName);
  const last = asText(identity.lastName);
  const merged = `${first} ${last}`.trim();
  if (merged) return merged;

  const name = asText(identity.name);
  if (name) return name;

  const email = asText(identity.email);
  return email || 'User';
}

const AvatarButton = styled.button<{ $size: number; $clickable: boolean }>`
  all: unset;
  box-sizing: border-box;
  width: ${({ $size }) => `${$size}px`};
  height: ${({ $size }) => `${$size}px`};
  border-radius: ${({ $size }) => `${Math.max(10, Math.round($size * 0.28))}px`};
  background: linear-gradient(165deg, #ecd1ab 0%, #dfba88 100%);
  color: #1a1a1a;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: ${({ $size }) => `${Math.max(11, Math.round($size * 0.34))}px`};
  font-weight: 900;
  overflow: hidden;
  flex: 0 0 auto;
  cursor: ${({ $clickable }) => ($clickable ? 'pointer' : 'default')};
  transition: transform 0.12s ease, box-shadow 0.12s ease;

  &:hover {
    transform: ${({ $clickable }) => ($clickable ? 'translateY(-1px)' : 'none')};
    box-shadow: ${({ $clickable }) =>
      $clickable ? '0 4px 10px rgba(0, 0, 0, 0.22)' : 'none'};
  }
`;

const AvatarImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
`;

type PortalAvatarProps = {
  className?: string;
  uid?: string | null;
  email?: string | null;
  firstName?: string | null;
  lastName?: string | null;
  name?: string | null;
  photoURL?: string | null;
  size?: number;
  title?: string;
  stopPropagation?: boolean;
  disableNavigation?: boolean;
  implicitSelfFallback?: boolean;
  onClick?: (event: MouseEvent<HTMLButtonElement>) => void;
};

export default function PortalAvatar({
  className,
  uid,
  email,
  firstName,
  lastName,
  name,
  photoURL,
  size = 40,
  title,
  stopPropagation = true,
  disableNavigation = false,
  implicitSelfFallback = true,
  onClick,
}: PortalAvatarProps) {
  const navigate = useNavigate();
  const { user, role } = useAuth();

  const identity = useMemo(
    () => ({ firstName, lastName, name, email }),
    [firstName, lastName, name, email],
  );
  const label = getPortalAvatarLabel(identity);
  const initials = getPortalAvatarInitials(identity);
  const refValue = asText(uid) || asText(email);
  const hasReference = Boolean(refValue);
  const selfUid = asText(user?.uid);
  const selfEmail = asText(user?.email).toLowerCase();
  const refLower = refValue.toLowerCase();
  const isSelf = hasReference
    ? refValue === selfUid || refLower === selfEmail
    : implicitSelfFallback;
  const photo =
    asText(photoURL) ||
    (isSelf && implicitSelfFallback ? asText(user?.photoURL) : '');
  const [imageFailed, setImageFailed] = useState(false);

  useEffect(() => {
    setImageFailed(false);
  }, [photo]);

  const canOpenOtherProfile = role === 'admin' || role === 'agent';
  const canNavigate =
    !disableNavigation &&
    ((hasReference && canOpenOtherProfile) || isSelf);

  const handleClick = (event: MouseEvent<HTMLButtonElement>) => {
    if (stopPropagation) event.stopPropagation();
    onClick?.(event);
    if (event.defaultPrevented || !canNavigate) return;

    if (isSelf) {
      navigate('/dashboard/profile');
      return;
    }
    if (refValue) {
      navigate(`/dashboard/users/profile/${encodeURIComponent(refValue)}`);
    }
  };

  const isInteractive = canNavigate || Boolean(onClick);

  return (
    <AvatarButton
      as={isInteractive ? 'button' : 'div'}
      type={isInteractive ? 'button' : undefined}
      className={className}
      $size={size}
      $clickable={isInteractive}
      aria-label={title || label}
      title={title || label}
      onClick={isInteractive ? handleClick : undefined}
      tabIndex={isInteractive ? 0 : -1}
    >
      {photo && !imageFailed ? (
        <AvatarImage
          src={photo}
          alt={label}
          loading="eager"
          onError={() => setImageFailed(true)}
        />
      ) : (
        initials
      )}
    </AvatarButton>
  );
}
